import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TrendingUp, TrendingDown, Target } from 'lucide-react';
import { formatCurrencyText } from '../../utils/chartFormatters';

/**
 * Funding Ratio Gauge Component
 * Visual representation of retirement funding status
 */
export default function FundingRatioGauge({ fundingRatio, requiredPortfolio, totalAtRetirement, currencySymbol = "$" }) {
  // Calculate status
  const percentage = Math.min(fundingRatio * 100, 150); // Cap display at 150%
  const actualPercentage = fundingRatio * 100;

  const getStatus = () => {
    if (fundingRatio >= 1.0) return { text: 'On Track', color: 'text-green-600', bgColor: 'bg-green-600', variant: 'success' };
    if (fundingRatio >= 0.75) return { text: 'Nearly There', color: 'text-yellow-600', bgColor: 'bg-yellow-600', variant: 'warning' };
    if (fundingRatio >= 0.5) return { text: 'Underfunded', color: 'text-orange-600', bgColor: 'bg-orange-600', variant: 'warning' };
    return { text: 'Critical Shortfall', color: 'text-red-600', bgColor: 'bg-red-600', variant: 'destructive' };
  };

  const status = getStatus();
  const gap = requiredPortfolio - totalAtRetirement;
  const hasShortfall = gap > 0;

  // Calculate gauge segments
  const getGaugeColor = (value) => {
    if (value >= 100) return '#10b981'; // green
    if (value >= 75) return '#f59e0b'; // yellow
    if (value >= 50) return '#f97316'; // orange
    return '#ef4444'; // red
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>Retirement Funding Ratio</CardTitle>
            <CardDescription>
              How well funded is your retirement plan?
            </CardDescription>
          </div>
          <Badge variant={status.variant} className="text-lg px-4 py-2">
            {status.text}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {/* Circular Gauge */}
        <div className="flex flex-col items-center justify-center py-8">
          {/* SVG Gauge */}
          <div className="relative w-64 h-64">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 200 200">
              {/* Background circle */}
              <circle
                cx="100"
                cy="100"
                r="80"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="20"
              />

              {/* Progress circle */}
              <circle
                cx="100"
                cy="100"
                r="80"
                fill="none"
                stroke={getGaugeColor(actualPercentage)}
                strokeWidth="20"
                strokeDasharray={`${(percentage / 100) * 502.65} 502.65`}
                strokeLinecap="round"
                className="transition-all duration-1000 ease-out"
              />

              {/* Tick marks */}
              {[0, 25, 50, 75, 100, 125, 150].map((tick) => {
                const angle = (tick / 150) * 360;
                const rad = (angle * Math.PI) / 180;
                const x1 = 100 + 70 * Math.cos(rad);
                const y1 = 100 + 70 * Math.sin(rad);
                const x2 = 100 + 80 * Math.cos(rad);
                const y2 = 100 + 80 * Math.sin(rad);

                return (
                  <line
                    key={tick}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke="#9ca3af"
                    strokeWidth="2"
                    className="transform rotate-90 origin-center"
                  />
                );
              })}
            </svg>

            {/* Center text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className={`text-5xl font-bold ${status.color}`}>
                {Math.round(actualPercentage)}%
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Funded
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-6 mt-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-600" />
              <span className="text-muted-foreground">0-50%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-600" />
              <span className="text-muted-foreground">50-75%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-600" />
              <span className="text-muted-foreground">75-100%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-600" />
              <span className="text-muted-foreground">100%+</span>
            </div>
          </div>
        </div>

        {/* Detailed Breakdown */}
        <div className="space-y-4 pt-6 border-t">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-muted rounded-lg">
              <div className="text-sm text-muted-foreground mb-1">Required Portfolio</div>
              <div className="text-2xl font-bold">
                {currencySymbol}{requiredPortfolio.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <Target className="h-3 w-3" />
                Based on 4% rule
              </div>
            </div>

            <div className="p-4 bg-muted rounded-lg">
              <div className="text-sm text-muted-foreground mb-1">Projected at Retirement</div>
              <div className={`text-2xl font-bold ${status.color}`}>
                {currencySymbol}{totalAtRetirement.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                {hasShortfall ? (
                  <>
                    <TrendingDown className="h-3 w-3" />
                    {currencySymbol}{Math.abs(gap).toLocaleString(undefined, { maximumFractionDigits: 0 })} short
                  </>
                ) : (
                  <>
                    <TrendingUp className="h-3 w-3" />
                    {currencySymbol}{Math.abs(gap).toLocaleString(undefined, { maximumFractionDigits: 0 })} surplus
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">Progress to Goal</span>
              <span className={`font-medium ${status.color}`}>
                {Math.round(actualPercentage)}%
              </span>
            </div>
            <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`absolute h-full ${status.bgColor} transition-all duration-1000 ease-out rounded-full`}
                style={{ width: `${Math.min(percentage, 100)}%` }}
              />
              {/* Goal line at 100% */}
              <div className="absolute h-full w-1 bg-gray-700" style={{ left: '100%', transform: 'translateX(-50%)' }} />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>{currencySymbol}0</span>
              <span className="absolute left-1/2 transform -translate-x-1/2">Goal</span>
              <span>{currencySymbol}{requiredPortfolio.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            </div>
          </div>
        </div>

        {/* Interpretation */}
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h4 className="text-sm font-semibold mb-2">What This Means</h4>
          <p className="text-sm text-muted-foreground">
            {fundingRatio >= 1.0 ? (
              <>
                Your retirement savings are <strong className="text-green-700">on track</strong>. Based on the 4% rule,
                your projected portfolio of {formatCurrencyText(totalAtRetirement, currencySymbol)} should support
                your desired retirement income with a good margin of safety.
              </>
            ) : fundingRatio >= 0.75 ? (
              <>
                You're <strong className="text-yellow-700">close to your goal</strong>. You need an additional {formatCurrencyText(Math.abs(gap), currencySymbol)} to fully fund your retirement.
                Consider increasing contributions or adjusting your retirement age.
              </>
            ) : fundingRatio >= 0.5 ? (
              <>
                Your plan is <strong className="text-orange-700">underfunded</strong>. You're {formatCurrencyText(Math.abs(gap), currencySymbol)} short of your goal.
                Significant changes to contributions, retirement age, or expenses may be needed.
              </>
            ) : (
              <>
                <strong className="text-red-700">Critical attention needed</strong>. Your projected savings will only cover {Math.round(fundingRatio * 100)}% of your retirement needs.
                Consider working with a financial advisor to restructure your retirement plan.
              </>
            )}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
